import { divide, multiply, round } from "utilities/math/math";

/**
 * Retainage %↔$ calculations, kept in a leaf module (only `utilities/math`) so both
 * `markupUtility` and `lineItemHelper` can depend on it. Putting these in `lineItemHelper` would
 * make `markupUtility` → `lineItemHelper` → `markupUtility` a cycle.
 *
 * The withheld $ is the persisted source of truth; the percent is ephemeral UI state re-derived
 * from it on load.
 */

/**
 * Compute the withheld $ from a percent of the line total: `% ÷ 100 × lineAmount`, rounded to
 * cents. Null percent → null (clears the $).
 *
 * Overloaded so a caller that has already ruled out a null percent gets `number` back rather than
 * `number | null` — that keeps the amount-change path assignable to entity line item types which
 * declare `retainageWithheld?: number`.
 */
export function calculateRetainageWithheldFromPercent(
    percent: number,
    lineAmount: number | null,
): number;
export function calculateRetainageWithheldFromPercent(
    percent: number | null,
    lineAmount: number | null,
): number | null;
export function calculateRetainageWithheldFromPercent(
    percent: number | null,
    lineAmount: number | null,
): number | null {
    if (percent === null || percent === undefined) {
        return null;
    }
    return round(multiply(divide(percent, 100), lineAmount ?? 0), 2);
}

/**
 * Re-derive the percent from a stored withheld $: `$ ÷ lineAmount × 100`, rounded to 2 dp. Null
 * withheld or zero line total → null (a 0 line can't back-derive a percent).
 */
export function calculateRetainagePercentFromWithheld(
    withheld: number | null,
    lineAmount: number | null,
): number | null {
    if (withheld === null || withheld === undefined || !lineAmount) {
        return null;
    }
    return round(multiply(divide(withheld, lineAmount), 100), 2);
}

/**
 * When a line's amount changes, hold the retention percent (the user's intent — "retain X%") and
 * recompute the withheld $ off the new line total, mirroring how markup % holds and re-derives its
 * amount. Returns `undefined` when the line carries no percent, so callers can spread the result
 * and no-op for non-retainage rows.
 */
export function recalculateRetainageOnAmountChange(
    retainagePercent: number | null | undefined,
    newBuilderCost: number,
): { retainageWithheld: number } | undefined {
    if (retainagePercent === null || retainagePercent === undefined) {
        return undefined;
    }
    return {
        retainageWithheld: calculateRetainageWithheldFromPercent(retainagePercent, newBuilderCost),
    };
}

/** Minimal row shape `recalculateRetainageFields` reads. */
export interface IRetainageRecalcRow {
    /** Must be the **post-edit** line total — callers that change an amount recompute it first. */
    builderCost: number;
    retainagePercent?: number | null;
    retainageWithheld?: number | null;
}

/**
 * The retainage fields a recalculation can write back.
 *
 * `retainageWithheld` stays non-nullable: the only path that writes it is an amount change, whose
 * percent is non-null by guard, so it never produces a null. Entity line item types that declare
 * `retainageWithheld?: number` therefore stay assignable. Clearing the $ to null goes through the
 * `retainagePercent` edit path, which is typed on `IRetainagePercentEditResult` below.
 */
export interface IRetainageRecalcResult {
    retainagePercent?: number | null;
    retainageWithheld?: number;
}

/** Result of editing the percent directly, which *can* clear the withheld $ to null. */
export interface IRetainagePercentEditResult {
    retainageWithheld: number | null;
}

/**
 * Re-derive the withheld $ after a line's amount changed, holding the row's retention percent.
 *
 * Prefer this over `recalculateRetainageFields` wherever the caller already knows an amount moved —
 * it takes no field name, so there's no question of whether that name means the field the user
 * edited or the one the function computes, and no magic string to typo into a silent no-op.
 * `recalculateRetainageFields` exists for callers whose edited field is genuinely dynamic.
 *
 * Returns `{}` for rows carrying no percent, so callers can spread it unconditionally.
 */
export function recalculateRetainageForAmountChange(
    row: IRetainageRecalcRow,
): IRetainageRecalcResult {
    return recalculateRetainageOnAmountChange(row.retainagePercent, row.builderCost) ?? {};
}

/**
 * Single entry point for retainage recalculation, mirroring markup's `recalculateAllFields`:
 * callers hand it the edited field plus the merged row and spread the result, instead of deciding
 * per-field which direction to derive.
 *
 * - editing `retainagePercent` → derive the withheld $ off the line total
 * - editing `retainageWithheld` → back-derive the percent off the line total
 * - editing an amount (`unitCost` / `quantity` / `builderCost`) → hold the percent and re-derive
 *   the withheld $ off `row.builderCost`, which the caller has already updated
 *
 * Returns `{}` for any other field, and for amount changes on rows carrying no percent, so a
 * caller can spread it unconditionally and non-retainage rows are untouched.
 */
export function recalculateRetainageFields(
    field: "retainagePercent",
    row: IRetainageRecalcRow,
): IRetainagePercentEditResult;
export function recalculateRetainageFields(
    field: string,
    row: IRetainageRecalcRow,
): IRetainageRecalcResult;
export function recalculateRetainageFields(
    field: string,
    row: IRetainageRecalcRow,
): IRetainageRecalcResult | IRetainagePercentEditResult {
    switch (field) {
        case "retainagePercent":
            return {
                retainageWithheld: calculateRetainageWithheldFromPercent(
                    row.retainagePercent ?? null,
                    row.builderCost,
                ),
            };
        case "retainageWithheld":
            return {
                retainagePercent: calculateRetainagePercentFromWithheld(
                    row.retainageWithheld ?? null,
                    row.builderCost,
                ),
            };
        case "unitCost":
        case "quantity":
        case "builderCost":
            return recalculateRetainageForAmountChange(row);
        default:
            return {};
    }
}
